import { useState, useEffect, useRef, useCallback } from "react";
import {
  getBenchmarkingSocialImpact,
  getBenchmarkingSocialImpactDetail,
} from "Explore/Services/Benchmarking";
import { buildBenchmarkingSocialImpactParams } from "../utils/socialImpactUtils";

/**
 * Custom hook to fetch and manage Benchmarking Social Impact (SDG) data
 * for Report Detail page.
 *
 * @param {Object} entity - Current selected entity
 * @param {Object} filters - Current report filters ({ yearRange, subjectArea })
 * @returns {Object} { data, isLoading, error, refetch, fetchDetail }
 */
export const useBenchmarkingSocialImpact = (entity, filters) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const lastFetchedParams = useRef(null);

  const params = buildBenchmarkingSocialImpactParams(entity, filters);
  const paramsKey = params ? JSON.stringify(params) : null;

  const fetchSocialImpact = useCallback(async () => {
    if (!params) {
      lastFetchedParams.current = null;
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    lastFetchedParams.current = paramsKey;
    setIsLoading(true);
    setError(null);

    try {
      const res = await getBenchmarkingSocialImpact(params);
      setData(res);
    } catch (err) {
      lastFetchedParams.current = null;
      console.error("Failed to fetch benchmarking social impact:", err);
      const errMsg =
        err?.response?.data?.detail?.errors?.[0]?.msg ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load social impact data.";
      setError(errMsg);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!params) {
      if (data !== null) setData(null);
      if (error !== null) setError(null);
      lastFetchedParams.current = null;
      return;
    }

    if (paramsKey === lastFetchedParams.current) {
      return;
    }

    fetchSocialImpact();
  }, [paramsKey, fetchSocialImpact]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDetail = useCallback(
    async (sdgNum, signal) => {
      if (!params || !sdgNum) return null;
      return getBenchmarkingSocialImpactDetail(sdgNum, params, signal);
    },
    [paramsKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    data,
    isLoading,
    error,
    refetch: fetchSocialImpact,
    fetchDetail,
  };
};

export default useBenchmarkingSocialImpact;
